import { ReactNode, useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import BrandBlock from "@/components/BrandBlock";
import Footer from "@/components/Footer";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeSwitcher from "@/components/ThemeSwitcher";
import InstallAppButton from "@/components/InstallAppButton";
import TennisBallMenuIcon from "@/components/TennisBallMenuIcon";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { PUBLIC_NAV, navLinkClass, HEADER_CTA_CLASS } from "@/lib/publicNav";

const NAV = PUBLIC_NAV;

interface Props {
  children: ReactNode;
  /** When true, content is centered with prose width; pass false for full-bleed landing. */
  contained?: boolean;
}

export default function PublicLayout({ children, contained = true }: Props) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-gradient-hero">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:bg-primary focus:text-primary-foreground focus:px-3 focus:py-1 focus:rounded"
      >
        Skip to content
      </a>
      <header className="border-b border-border/60 bg-card/60 backdrop-blur-xl sticky top-0 z-40 public-header">
        <div className="container flex items-center justify-between gap-4 py-1 sm:py-3 lscompact:!py-1">
          <Link to="/" className="flex shrink-0 items-center" aria-label="Tennis Log, My Tennis Ledger">
            <BrandBlock size={50} logoClassName="public-logo sm:!w-[60px] sm:!h-[60px] md:!w-[68px] md:!h-[68px]" />
          </Link>

          {/* Mobile: compact 2x2 control grid (install / menu / language / theme).
              In compact landscape the same controls collapse into one short row. */}
          <div className="grid grid-cols-2 gap-1 gap-y-0.5 shrink-0 lg:hidden lscompact:!flex lscompact:items-center lscompact:gap-1">
            <div className="w-14 h-11 lscompact:!w-11 lscompact:!h-9 flex items-center justify-center">
              <InstallAppButton
                variant="header"
                className="!h-11 !w-12 lscompact:!h-9 lscompact:!w-10 !px-0 justify-center rounded-lg border border-primary/40 bg-secondary/60 bg-gradient-to-br from-primary/40 to-accent/40 text-primary shadow-none transition hover:bg-primary/10 active:scale-95"
              />
            </div>
            <div className="w-14 h-11 lscompact:!w-11 lscompact:!h-9 flex items-center justify-center">
              <button
                type="button"
                aria-label="Toggle navigation"
                aria-expanded={open}
                aria-controls="mobile-nav"
                onClick={() => setOpen((v) => !v)}
                className="inline-flex items-center justify-center w-11 h-11 lscompact:w-11 lscompact:h-9 rounded-full border border-primary/40 bg-secondary/60 text-primary hover:bg-primary/10 active:scale-95 transition shadow-glow"
              >
                {open ? <X className="w-5 h-5" /> : <TennisBallMenuIcon className="w-9 h-9 lscompact:w-8 lscompact:h-8" />}
              </button>
            </div>
            <div className="flex items-center justify-center w-14 h-11 lscompact:w-11 lscompact:h-9 rounded-lg border border-border/60 bg-secondary/40 [&_button]:w-full [&_button]:min-w-0 [&_button]:px-0">
              <LanguageSwitcher />
            </div>
            <div className="flex items-center justify-center w-14 h-11 lscompact:w-11 lscompact:h-9 rounded-lg border border-border/60 bg-secondary/40 [&_button]:w-full [&_button]:min-w-0 [&_button]:px-0">
              <ThemeSwitcher />
            </div>
          </div>

          {/* Desktop controls: two-row action cluster.
              Row 1: Language switcher + Theme toggle.
              Row 2: Download app + Sign in/Open app. */}
          <div className="hidden lg:flex lscompact:!hidden flex-col items-end gap-1.5 shrink-0">
            <div className="flex items-center gap-0.5 rounded-xl border border-border/60 bg-secondary/40 px-0.5 [&_button]:min-h-9 [&_button]:min-w-9 [&_button]:h-9">
              <LanguageSwitcher />
              <span className="w-px h-5 bg-border/70" aria-hidden="true" />
              <ThemeSwitcher />
            </div>
            <div className="flex items-center gap-2">
              <InstallAppButton variant="header" className={HEADER_CTA_CLASS} />
              <Button
                asChild
                className={`${HEADER_CTA_CLASS} bg-gradient-primary text-primary-foreground shadow-glow`}
              >
                <Link to={user ? "/app/matches" : "/auth"}>
                  {user ? t("publicChrome.openApp") : t("publicChrome.signIn")}
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Desktop / tablet: navigation always gets its own full-width row. */}
        <nav
          className="container hidden lg:flex flex-wrap lscompact:!hidden items-center justify-center gap-1 pb-2"
          aria-label="Primary"
        >
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => navLinkClass(isActive)}>
              {n.label}
            </NavLink>
          ))}
        </nav>

        {open && (
          <nav id="mobile-nav" className="lg:hidden border-t border-border/60 bg-card/80 backdrop-blur lscompact:max-h-[60vh] lscompact:overflow-y-auto" aria-label="Mobile">
            <div className="container py-2 grid grid-cols-2 gap-1">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-md text-sm font-medium ${
                      isActive
                        ? "bg-secondary text-primary"
                        : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <Link
                to={user ? "/app/matches" : "/auth"}
                className="px-3 py-2 rounded-md text-sm font-semibold bg-gradient-primary text-primary-foreground text-center col-span-2 mt-1"
              >
                {user ? t("publicChrome.openApp") : t("publicChrome.signIn")}
              </Link>
            </div>
          </nav>
        )}
      </header>

      <main id="main-content" className={contained ? "flex-1 container py-10 lscompact:py-3 animate-fade-in" : "flex-1"}>
        {children}
      </main>

      <Footer />
    </div>
  );
}

